import { useEffect, useState } from 'react';

/**
 * Single source of truth for the phone breakpoint, shared by every component
 * that has to make the same call in JS that the stylesheets make in CSS.
 *
 * Driven off matchMedia rather than window.innerWidth, and reactive to
 * rotation / zoom / resize. innerWidth is an integer while the media query
 * sees the fractional viewport, so comparing `innerWidth < 576` opens a
 * 575.5–575.98px dead zone where CSS has switched to the phone layout but JS
 * still believes it is on desktop. That mismatch already cost us one bug (the
 * connector referral row became unopenable), so it is fixed in one place here.
 *
 * Must stay in step with the CSS breakpoint (`max-width: 575.98px`).
 */
export const PHONE_MQ = '(max-width: 575.98px)';

/**
 * Reactive matchMedia for any query. Components whose CSS switches layout at
 * a breakpoint other than PHONE_MQ (the connector referral cards, QA 8636)
 * pass their own query so JS and CSS stay in step for them too.
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    // Re-read on mount: the viewport can change between first render and the
    // effect firing (and SSR/first paint starts from the `false` default).
    setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export default function useIsPhone() {
  return useMediaQuery(PHONE_MQ);
}
